import type { AlloyLegacyThemeDesignTokens, ThemeTypography } from '../types';
import type { Weave3DesignTokens } from '../types/weave3Types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

/** No token source, identical in every mode. */
const TRUNCATION = {
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  lineClamp: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
} as const;

export const getTypographyTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): ThemeTypography => ({
  ...theme.typography,
  'font-family': theme.typography.bodyMedium.fontFamily,
});

export const getTypographyTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): ThemeTypography => {
  const fontSize = tokens?.generic?.['font-size'];
  const lineHeight = tokens?.generic?.['line-height'];

  return {
    ...tokens?.component?.typography,
    'font-family': fontFamily,
    hero: {
      fontFamily,
      fontWeight: 400,
      fontSize: fontSize[48],
      lineHeight: '64px',
    },
    title: {
      fontFamily,
      fontWeight: 400,
      fontSize: fontSize[30],
      lineHeight: '42px',
    },
    heading1: {
      fontFamily,
      fontWeight: 400,
      fontSize: fontSize[24],
      lineHeight: lineHeight[30],
    },
    heading2: {
      fontFamily,
      fontWeight: 400,
      fontSize: fontSize[20],
      lineHeight: lineHeight[26],
    },
    heading3: {
      fontFamily,
      fontWeight: 600,
      fontSize: fontSize[16],
      lineHeight: lineHeight[22],
    },
    heading4: {
      fontFamily,
      fontWeight: 700,
      fontSize: fontSize[14],
      lineHeight: lineHeight[20],
    },
    heading5: {
      fontFamily,
      fontWeight: 700,
      fontSize: fontSize[12],
      lineHeight: lineHeight[16],
    },
    bodyLarge: {
      fontFamily,
      fontWeight: 500,
      fontSize: fontSize[16],
      lineHeight: lineHeight[22],
    },
    bodyLargeBold: {
      fontFamily,
      fontWeight: 700,
      fontSize: fontSize[16],
      lineHeight: lineHeight[22],
    },
    bodyMedium: {
      fontFamily,
      fontWeight: 500,
      fontSize: fontSize[14],
      lineHeight: lineHeight[20],
    },
    bodyMediumBold: {
      fontFamily,
      fontWeight: 700,
      fontSize: fontSize[14],
      lineHeight: lineHeight[20],
    },
    bodySmall: {
      fontFamily,
      fontWeight: 500,
      fontSize: fontSize[12],
      lineHeight: lineHeight[16],
    },
    bodySmallBold: {
      fontFamily,
      fontWeight: 700,
      fontSize: fontSize[12],
      lineHeight: lineHeight[16],
    },
    labelMedium: {
      fontFamily,
      fontWeight: 600,
      fontSize: fontSize[14],
      lineHeight: lineHeight[20],
    },
    labelSmall: {
      fontFamily,
      fontWeight: 600,
      fontSize: fontSize[12],
      lineHeight: lineHeight[16],
    },
    caption: {
      fontFamily,
      fontWeight: 500,
      fontSize: fontSize[11],
      lineHeight: '15px',
    },
    captionBold: {
      fontFamily,
      fontWeight: 700,
      fontSize: fontSize[11],
      lineHeight: '15px',
    },
    eyebrow: {
      fontFamily,
      fontWeight: 700,
      fontSize: '10px',
      lineHeight: '15px',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
    },
    ...TRUNCATION,
  };
};

/**
 * Weave 3 ships two five-step ramps (`fixed` for chrome, `variable` for
 * content) against Alloy's eighteen roles, so the roles are the contract and
 * the ramp is only a source. Alloy's literal stands wherever W3 has no matching
 * step: `hero`/`heading2`/`eyebrow` (no size), the line-heights W3 pairs looser
 * than Alloy, and the four `400` weights (W3's lightest is 500).
 */
export const getTypographyTokensWeave3 = (
  tokens?: Weave3DesignTokens,
  fontFamily = 'Artifakt Element, sans-serif',
): ThemeTypography => {
  const fixedSize = tokens?.semantic?.['font-size']?.fixed;
  const fixedLine = tokens?.semantic?.['line-height']?.fixed;
  const variableSize = tokens?.semantic?.['font-size']?.variable;
  const weights = tokens?.generic?.['font-weight'];

  const weight = (value?: string, fallback?: number) =>
    value === undefined ? fallback : Number(value);

  const regular = weight(weights?.regular, 500);
  const medium = weight(weights?.medium, 600);
  const bold = weight(weights?.bold, 700);

  return {
    'font-family': fontFamily,
    hero: {
      fontFamily,
      fontWeight: 400,
      fontSize: '48px',
      lineHeight: '64px',
    },
    title: {
      fontFamily,
      fontWeight: 400,
      fontSize: fixedSize?.xxl,
      lineHeight: '42px',
    },
    heading1: {
      fontFamily,
      fontWeight: 400,
      fontSize: fixedSize?.xl,
      lineHeight: fixedLine?.xl,
    },
    heading2: {
      fontFamily,
      fontWeight: 400,
      fontSize: '20px',
      lineHeight: '26px',
    },
    heading3: {
      fontFamily,
      fontWeight: medium,
      fontSize: variableSize?.l,
      lineHeight: '22px',
    },
    heading4: {
      fontFamily,
      fontWeight: bold,
      fontSize: fixedSize?.l,
      lineHeight: fixedLine?.l,
    },
    heading5: {
      fontFamily,
      fontWeight: bold,
      fontSize: fixedSize?.m,
      lineHeight: fixedLine?.m,
    },
    bodyLarge: {
      fontFamily,
      fontWeight: regular,
      fontSize: variableSize?.l,
      lineHeight: '22px',
    },
    bodyLargeBold: {
      fontFamily,
      fontWeight: bold,
      fontSize: variableSize?.l,
      lineHeight: '22px',
    },
    bodyMedium: {
      fontFamily,
      fontWeight: regular,
      fontSize: fixedSize?.l,
      lineHeight: fixedLine?.l,
    },
    bodyMediumBold: {
      fontFamily,
      fontWeight: bold,
      fontSize: fixedSize?.l,
      lineHeight: fixedLine?.l,
    },
    bodySmall: {
      fontFamily,
      fontWeight: regular,
      fontSize: fixedSize?.m,
      lineHeight: fixedLine?.m,
    },
    bodySmallBold: {
      fontFamily,
      fontWeight: bold,
      fontSize: fixedSize?.m,
      lineHeight: fixedLine?.m,
    },
    labelMedium: {
      fontFamily,
      fontWeight: medium,
      fontSize: fixedSize?.l,
      lineHeight: fixedLine?.l,
    },
    labelSmall: {
      fontFamily,
      fontWeight: medium,
      fontSize: fixedSize?.m,
      lineHeight: fixedLine?.m,
    },
    caption: {
      fontFamily,
      fontWeight: regular,
      fontSize: fixedSize?.s,
      lineHeight: '15px',
    },
    captionBold: {
      fontFamily,
      fontWeight: bold,
      fontSize: fixedSize?.s,
      lineHeight: '15px',
    },
    eyebrow: {
      fontFamily,
      fontWeight: bold,
      fontSize: '10px',
      lineHeight: '15px',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
    },
    ...TRUNCATION,
  };
};
